import React, { useRef, useEffect, useState, useCallback } from 'react';
import './ScratchCard.css';
import { useT } from '../i18n';

const SCRATCH_THRESHOLD = 60; // Seuil de 60% pour considérer la carte comme "grattée"
// Délai laissé pour lire le message en entier (révélé à 100% automatiquement dès le seuil
// atteint) avant que le bandeau de remerciement ne prenne la place du cœur.
const REVEAL_TO_CTA_DELAY_MS = 3500;
// Espace additionnel réservé au titre visible (pousse le cœur vers le bas), cf. ScratchCard.js.
const TITRE_OFFSET_PX = 220;

const ScratchCardTracked = ({
  width,
  height,
  imageSrc,
  gratteSrc,
  brushSize,
  text,
  titre,
  textSize,
  textColor,
  marginTop,
  lineHeight,
  textHeight,
  onScratchComplete,  // Callback quand le seuil de 80% est atteint
  ctaContent,         // JSX affiché à la place du cœur une fois la carte lue (remerciement + CTA)
}) => {
  const t = useT('scratch-card');
  const canvasRef = useRef(null);
  const gratteRef = useRef(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [scratchedPercent, setScratchedPercent] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false); // Éviter les appels multiples
  const [showCta, setShowCta] = useState(false); // Le bandeau a remplacé le cœur

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const image = gratteRef.current;
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      context.drawImage(image, 0, 0);
      context.globalCompositeOperation = 'destination-out';
    };
  }, [gratteSrc]);

  // Calculer le pourcentage gratté (appelé UNIQUEMENT au relâchement)
  const calculateScratchedPercent = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    const pixels = context.getImageData(0, 0, width, height).data;
    const totalPixels = width * height;
    let scratchedPixels = 0;

    for (let i = 0; i < pixels.length; i += 4) {
      if (pixels[i + 3] === 0) {
        scratchedPixels++;
      }
    }

    const percentage = (scratchedPixels / totalPixels) * 100;
    setScratchedPercent(percentage);

    // Seuil atteint (une seule fois) : on termine le grattage pour l'utilisateur (révèle le
    // message à 100%, plus propre qu'un tas de petits trous), on prévient la page (tracking +
    // enregistrement), puis on laisse le temps de lire avant de remplacer le cœur par le CTA.
    if (percentage >= SCRATCH_THRESHOLD && !isCompleted) {
      setIsCompleted(true);
      context.clearRect(0, 0, width, height);
      setScratchedPercent(100);
      if (onScratchComplete) onScratchComplete(percentage);
      if (ctaContent) {
        setTimeout(() => setShowCta(true), REVEAL_TO_CTA_DELAY_MS);
      }
    }
  }, [width, height, onScratchComplete, isCompleted, ctaContent]);

  const handleMouseDown = () => {
    setIsMouseDown(true);
  };

  const handleMouseUp = useCallback(() => {
    if (isMouseDown) {
      setIsMouseDown(false);
      calculateScratchedPercent();
    }
  }, [isMouseDown, calculateScratchedPercent]);

  const handleMouseMove = (e) => {
    if (!isMouseDown) return;
    scratch(e.clientX, e.clientY);
  };

  const handleTouchStart = (e) => {
    setIsMouseDown(true);
    const touch = e.touches[0];
    scratch(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e) => {
    if (!isMouseDown) return;
    const touch = e.touches[0];
    scratch(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => {
    if (isMouseDown) {
      setIsMouseDown(false);
      calculateScratchedPercent();
    }
  };

  // Fonction de grattage SANS calcul de pourcentage (optimisé)
  const scratch = (x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const offsetX = x - rect.left;
    const offsetY = y - rect.top;

    context.beginPath();
    context.arc(offsetX, offsetY, brushSize, 0, Math.PI * 2, true);
    context.fill();
    // PAS de calcul ici - seulement au relâchement
  };

  // marginTop vient de l'API (chaîne de caractères, ex. "70") : Number() est indispensable
  // avant tout `+`, sinon "70" + 60 concatène en "7060" au lieu d'additionner en 130.
  const baseAdjustedMarginTop = gratteSrc?.includes('rond') ? Number(marginTop) - 30 : Number(marginTop);
  const adjustedMarginTop = titre ? baseAdjustedMarginTop + TITRE_OFFSET_PX : baseAdjustedMarginTop;

  return (
    <div className="scratch-card-container">
      <div
        className="mobile-container"
        style={{ backgroundImage: `url(${imageSrc})` }}
      >
        {titre && (
          <div className="scratch-card-titre">
            {titre}
          </div>
        )}
        <div
          className="scratch-card-canvas-container"
          style={{
            marginTop: `${adjustedMarginTop}px`,
            opacity: showCta ? 0 : 1,
            transition: 'opacity 0.6s ease',
            pointerEvents: showCta ? 'none' : 'auto',
          }}
        >
          <canvas
            ref={canvasRef}
            width={width}
            height={height}
            className="scratch-card-canvas"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ touchAction: 'none' }}
          />
          <img
            ref={gratteRef}
            src={gratteSrc}
            alt={t('scratch-card.scratchLayerAlt')}
            style={{ display: 'none' }}
          />
        </div>

        <div
          className="scratch-card-text"
          style={{
            fontSize: `${textSize}px`,
            color: textColor,
            marginTop: `${Number(marginTop) + Number(textHeight) + (titre ? TITRE_OFFSET_PX : 0)}px`,
            lineHeight: `${lineHeight}px`,
            opacity: showCta ? 0 : 1,
            transition: 'opacity 0.6s ease',
          }}
        >
          <p dangerouslySetInnerHTML={{ __html: text.replace(/\n/g, '<br />') }} />
        </div>

        {/* Bandeau de remerciement + CTA : prend exactement la place du cœur une fois la
            carte lue (même position que .scratch-card-canvas-container). */}
        {ctaContent && (
          <div
            style={{
              position: 'absolute',
              marginTop: `${adjustedMarginTop}px`,
              width: 320,
              maxWidth: '85vw',
              opacity: showCta ? 1 : 0,
              transform: showCta ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s',
              pointerEvents: showCta ? 'auto' : 'none',
              zIndex: 3,
            }}
          >
            {ctaContent}
          </div>
        )}

        {/* Indicateur de progression, masqué une fois le CTA affiché */}
        {scratchedPercent > 0 && !showCta && (
          <div
            className="scratch-progress"
            style={{
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              background: isCompleted ? 'rgba(76, 175, 80, 0.8)' : 'rgba(0,0,0,0.5)',
              color: 'white',
              padding: '5px 10px',
              borderRadius: '5px',
              fontSize: '12px',
              transition: 'background 0.3s ease'
            }}
          >
            {Math.round(scratchedPercent)}%
            {isCompleted && ' ✓'}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScratchCardTracked;
